import React, { useEffect, useRef, useState } from "react";

const TotalIndemnifications = ({
  target = 110_000_000, // valor alvo (padrão = 110 milhões)
  duration = 7000, // duração da animação em ms
  repeat = false, // se true, repete a animação enquanto visível
  repeatDelay = 5000, // intervalo entre repetições (ms)
}) => {
  const [count, setCount] = useState(0);
  const containerRef = useRef(null);
  const animRef = useRef(null);
  const repeatTimerRef = useRef(null);
  const hasAnimatedRef = useRef(false);

  // ✅ Agora exibe com centavos (R$ 110.000.000,00)
  const formatCurrency = (value) =>
    value.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const animateCount = (from = 0, to = target, ms = duration) => {
    if (animRef.current) cancelAnimationFrame(animRef.current);
    const startTime = performance.now();

    const step = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / ms, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      const current = from + (to - from) * eased;
      setCount(current);

      if (progress < 1) {
        animRef.current = requestAnimationFrame(step);
      } else {
        animRef.current = null;
      }
    };

    animRef.current = requestAnimationFrame(step);
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      animateCount();
      hasAnimatedRef.current = true;
      return;
    }

    const onIntersect = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          if (!hasAnimatedRef.current || repeat) {
            animateCount(0, target, duration);
            hasAnimatedRef.current = true;

            if (repeat) {
              if (repeatTimerRef.current) clearInterval(repeatTimerRef.current);
              repeatTimerRef.current = setInterval(() => {
                animateCount(0, target, duration);
              }, repeatDelay);
            }
          }
        } else {
          if (repeat && repeatTimerRef.current) {
            clearInterval(repeatTimerRef.current);
            repeatTimerRef.current = null;
          }
        }
      });
    };

    const obs = new IntersectionObserver(onIntersect, {
      root: null,
      rootMargin: "0px",
      threshold: 0.25,
    });

    obs.observe(el);

    return () => {
      obs.disconnect();
      if (animRef.current) cancelAnimationFrame(animRef.current);
      if (repeatTimerRef.current) clearInterval(repeatTimerRef.current);
    };
  }, [target, duration, repeat, repeatDelay]);

  return (
    <div ref={containerRef} className="mx-auto my-12 bg-white shadow-lg rounded-lg p-8">
      <div className="space-y-6 max-w-5xl mx-auto pb-6">
        <p className="text-lg sm:text-xl text-blue-950 text-justify">
          Ao longo de nossa trajetória, já pagamos{" "}
          <span className="font-bold text-orange-500">mais de R$ 110 milhões</span>{" "}
          em indenizações, garantindo a confiança e a tranquilidade de nossos
          clientes. Cada pagamento reflete nosso compromisso com{" "}
          <span className="font-bold">transparência, segurança e excelência</span>{" "}
          no atendimento.
        </p>
      </div>

      <div className="bg-blue-950 text-white rounded-xl shadow-lg pt-8 pb-8 text-center transform hover:scale-105 transition-transform duration-300 max-w-4xl mx-auto">
        <h3 className="text-sm xs:text-2xl font-semibold mb-2">
          Indenizações Pagas
        </h3>

        <p className="text-2xl xs:text-4xl sm:text-5xl font-bold text-orange-400">
          {formatCurrency(count)}
        </p>

        <p className="mt-2 text-xs xs:text-lg sm:text-xl">
          Garantindo a confiança e tranquilidade <br />de nossos clientes
        </p>
      </div>
    </div>
  );
};

export default TotalIndemnifications;
